/**
 * Feedback lives on pointer-*down*, not on click.
 *
 * Waiting for touch-up to show a press state is the single most common way a
 * web interface gives itself away. The scale here is deliberately small (0.97)
 * and fast (100ms) — big enough to feel, too small to look like an animation.
 */

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { Link, type LinkProps } from 'react-router';

type Variant = 'primary' | 'secondary' | 'quiet' | 'destructive';
type Size = 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  children: ReactNode;
}

const base =
  'relative inline-flex items-center justify-center gap-2 rounded-full font-[560] ' +
  'select-none transition-[transform,background-color,opacity] duration-100 ease-out ' +
  'active:scale-[0.97] disabled:opacity-40 disabled:pointer-events-none ' +
  'motion-reduce:active:scale-100';

const variants: Record<Variant, string> = {
  primary: 'bg-accent text-ink-0 hover:bg-accent-strong',
  secondary: 'bg-ink-2 text-ink-7 hover:bg-ink-3',
  quiet: 'bg-transparent text-ink-5 hover:text-ink-7 hover:bg-ink-1',
  destructive: 'bg-transparent text-action border border-action/40 hover:bg-action/10',
};

const sizes: Record<Size, string> = {
  md: 'h-11 px-5 text-[0.9375rem]',
  lg: 'h-14 px-7 text-[1.0625rem]',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', className = '', children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={`${base} ${variants[variant]} ${sizes[size]} ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
});

/**
 * A navigation target that looks like a button.
 *
 * Separate from `Button` rather than an `as` prop because the two are not
 * interchangeable to a screen reader or to a middle click: one performs an
 * action, the other goes somewhere.
 */
export function LinkButton({
  variant = 'primary',
  size = 'md',
  className = '',
  children,
  ...rest
}: LinkProps & { variant?: Variant; size?: Size; children: ReactNode }) {
  return (
    <Link className={`${base} ${variants[variant]} ${sizes[size]} ${className}`} {...rest}>
      {children}
    </Link>
  );
}
